/**
 * This script provides functionality for all search boxes with the suggest feature.
 */

import $ from 'jquery';
import debounce from 'lodash.debounce';
import { buildUrl, searchQueryStringKeys, setSearchParam } from './search';

export const init = ($target) => {
	const $suggestLists = $('.js-search-box .js-suggest-list');

	if (!$suggestLists.length) {
		return;
	}

	const SELECTED_CLASS = 'suggest__item--focused',
		DEBOUNCE_WAIT_MS = 500,
		DOWN_ARROW_KEY_CODE = 40,
		UP_ARROW_KEY_CODE = 38,
		ESCAPE_KEY_CODE = 27,
		TAB_KEY_CODE = 9,
		ENTER_KEY_CODE = 13,
		SPACEBAR_KEY_CODE = 0 || 32;

	attachCallbacks();

	function attachCallbacks() {
		// prevent click to hide the suggest list (due to click outside)
		$suggestLists.on('click', function (event) {
			event.stopPropagation();
		});

		// click outside
		$target.on('click', hideAllLists);

		$suggestLists.each(function () {
			const $suggestList = $(this),
				$searchBox = $(this).closest('.js-search-box'),
				$input = $searchBox.find('input[name=queryText]');

			// show list on focus
			$input.on('focus', function () {
				$input.trigger('input');
				$input.trigger('select');
			});

			// on key down
			$input.on('keydown', onKeyDown);

			// prevent click to hide the suggest list (due to click outside)
			$input.on('click', function (event) {
				event.stopPropagation();
			});

			// callbacks for hero search and error404
			if ($searchBox.hasClass('js-suggest--global-search')) {
				$input.on('input', debounce(onGlobalSearchInput, DEBOUNCE_WAIT_MS));

				$input.on('input', function () {
					if ($input.val().length >= 3) $input.parent().removeClass('has-error');
				});

				$searchBox.on('submit', function (event) {
					if ($input.val().length < 3) {
						event.preventDefault();
						$input.parent().addClass('has-error');
						return;
					}

					hideSuggestList($suggestList);
				});
			}
		});
	}

	/**
	 * Provides common functionality for suggest.
	 * Enable by adding the 'js-suggest--global-search' class to search box.
	 * @param {object} event
	 */
	function onGlobalSearchInput(event) {
		const $input = $(event.target),
			$segmentInput = $input.siblings('input[name=segment]'),
			$searchBox = $input.closest('.js-search-box'),
			baseUrl = $searchBox.data('suggest-base-url'),
			$categoriesInput = $input.siblings('input[name=categories]'),
			$childOnlyInput = $input.siblings('input[name=childOnly]');

		if (!$input.length || !$segmentInput.length || !$searchBox.length) {
			return;
		}

		const params = {
			queryText: $input.val().trim(),
			segment: $segmentInput.val(),
			categories: $categoriesInput.val(),
			childOnly: !!$childOnlyInput.val(),
		};

		let url = `${baseUrl}/${encodeURIComponent(params.queryText)}/${params.segment}`;

		const urlSearchParams = new URLSearchParams();
		setSearchParam(urlSearchParams, searchQueryStringKeys.categories, params.categories);
		setSearchParam(urlSearchParams, searchQueryStringKeys.childOnly, params.childOnly);

		url = buildUrl(url, urlSearchParams);

		onInput($input, url);
	}

	function onKeyDown(event) {
		const keyCode = event.which;

		if (
			keyCode !== DOWN_ARROW_KEY_CODE &&
			keyCode !== UP_ARROW_KEY_CODE &&
			keyCode !== ENTER_KEY_CODE &&
			keyCode !== ESCAPE_KEY_CODE &&
			keyCode !== TAB_KEY_CODE &&
			keyCode !== SPACEBAR_KEY_CODE
		) {
			return;
		}

		const $input = $(event.target),
			$searchBox = $input.closest('.js-search-box'),
			$suggestList = $searchBox.find('.js-suggest-list'),
			$suggestItems = $suggestList.find('.suggest__item'),
			$focusedItem = $suggestList.find(`.${SELECTED_CLASS}`),
			maxIndex = $suggestItems.length - 1,
			selectedItemIndex = $suggestItems.index($focusedItem);

		switch (keyCode) {
			case SPACEBAR_KEY_CODE:
				if (!$focusedItem.length) {
					return;
				}

				// prevent adding an extra space
				event.preventDefault();

				selectItem($focusedItem, true);
				hideSuggestList($suggestList);
				return;

			case TAB_KEY_CODE:
				if (!$focusedItem.length) {
					hideSuggestList($suggestList);
					return;
				}

				selectItem($focusedItem, true);
				hideSuggestList($suggestList);
				return;

			case ENTER_KEY_CODE:
				if (!$focusedItem.length) {
					return;
				}

				// prevent form submit
				event.preventDefault();

				selectItem($focusedItem, false);
				return;

			case ESCAPE_KEY_CODE:
				// prevent text clear
				event.preventDefault();

				focusOut($focusedItem);
				hideSuggestList($suggestList);
				return;

			case DOWN_ARROW_KEY_CODE:
			case UP_ARROW_KEY_CODE:
				// prevent the cursor from moving to input start
				event.preventDefault();

				showSuggestList($suggestList);

				let nextIndex = keyCode === DOWN_ARROW_KEY_CODE ? selectedItemIndex + 1 : selectedItemIndex - 1;

				if (nextIndex > maxIndex) {
					return;
				}

				focusOut($focusedItem);

				if (nextIndex < 0) {
					return;
				}

				const $nextItem = $suggestItems.eq(nextIndex);
				focusIn($nextItem);
				return;

			default:
				return;
		}

		function focusIn($item) {
			$item.addClass(SELECTED_CLASS);
			$item.attr('aria-selected', true);
			$input.attr('aria-activedescendant', $item.attr('id'));
		}

		function focusOut($item) {
			$item.removeClass(SELECTED_CLASS);
			$item.attr('aria-selected', false);
			$input.removeAttr('aria-activedescendant');
		}

		function selectItem($item, preventSubmit) {
			$item.find('.suggest__link').trigger('click', preventSubmit);
			focusOut($item);
		}
	}

	function hideAllLists() {
		$suggestLists.each(function () {
			hideSuggestList($(this));
		});
	}
};

function setStatusText($status, text) {
	setTimeout(function () {
		$status.text(text);
	}, 500);
}

export async function onInput($input, url) {
	const $searchBox = $input.closest('.js-search-box'),
		$suggestList = $searchBox.find('.js-suggest-list'),
		$status = $searchBox.find('.js-suggest-status'),
		query = $input.val().trim();

	if (query.length < 3) {
		hideSuggestList($suggestList);
		$suggestList.empty();
		setStatusText($status, $status.data('invalid-input'));
		return;
	}

	let res = '';
	try {
		res = await sendRequest(url);
	} catch (err) {
		console.error(err.statusText);
		return;
	}

	fillSuggestList($suggestList, res);
	showSuggestList($suggestList);
	attachSuggestListItemsCallbacks($suggestList);

	async function sendRequest(url) {
		return $.ajax({
			type: 'GET',
			url: url,
			contentType: 'text/plain',
		});
	}

	function attachSuggestListItemsCallbacks($suggestList) {
		const $searchBox = $suggestList.closest('.js-search-box'),
			$input = $searchBox.find('input[name=queryText]');

		$suggestList.find('.suggest__link').on('click', function (event, preventSubmit) {
			$input.val($(this).text().trim());
			hideSuggestList($suggestList);
			if (!preventSubmit) $searchBox.trigger('submit');
		});
	}

	async function fillSuggestList($suggestList, res) {
		const $searchBox = $suggestList.closest('.js-search-box'),
			$status = $searchBox.find('.js-suggest-status'),
			searchBoxId = $searchBox.attr('id');

		if (!res || !res.length) {
			$suggestList.empty();
			setStatusText($status, $status.data('no-results'));
			return;
		}

		const suggestItem = (item, idx) => `
			<li class="suggest__item" 
				role="option" 
				tabindex="-1" 
				aria-selected="false"
				aria-posinset="${idx + 1}"
				aria-setsize="${res.length}"
				id="${searchBoxId}__suggest__item-${idx}">
				<span class="suggest__link">${item}</span>
			</li>`;

		const suggestListHtml = res.map((item, idx) => suggestItem(item, idx)).join('');
		$suggestList.html(suggestListHtml);

		let statusText = $status.data('found');
		if (statusText) {
			statusText = statusText.replace('$', res.length);
			setStatusText($status, statusText);
		}
	}
}

function showSuggestList($suggestList) {
	if (!$suggestList.children().length) {
		return;
	}

	const $input = $suggestList.closest('.js-search-box').find('input[role=combobox]');

	if (!$input.is(':focus')) {
		return;
	}

	$input.attr('aria-expanded', 'true');

	$suggestList.removeClass('suggest__list--hidden');
}

function hideSuggestList($suggestList) {
	const $input = $suggestList.closest('.js-search-box').find('input[role=combobox]');

	$input.attr('aria-expanded', 'false');
	$suggestList.addClass('suggest__list--hidden');
}
